import React from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SLOTS, STATUS_OPTIONS } from "../lib/slots";
import {
  PAYMENT_STATUS_OPTIONS, DEFAULT_PAYMENT_LABELS,
  emptyPayment, emptyServiceContract,
  normalizePayment, normalizeServiceContract,
  paymentToPayload, serviceContractToPayload,
  addDaysIso,
} from "../lib/finance";
import { ChevronDown, ChevronUp, Trash2, RotateCcw } from "lucide-react";
import { DateField } from "./DateField";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { reconcileAutoNotes, removeOpenAutoNotes, dropOrphanAutoNotes, applyM1Sent, deadlineLabel } from "../lib/reminders";
import { PLANNING_ICON_OPTIONS, PLANNING_ICON_VALUES, PlanningIcon } from "../lib/planningIcons";

// Verlustfreie Normalisierung eingehender Planungsnotizen aus dem Backend.
// Kritisch: den Backend-Wert `planner_icon` unverändert durchreichen (nur
// bei fehlendem/leerem Wert Default "production"). Radix Select darf nie
// zwischenzeitlich einen anderen Wert halten — sonst schreibt sein
// `onValueChange` beim Umschalten der Row-Daten den falschen Wert zurück.
function normalizePlanningNotes(notes) {
  return (Array.isArray(notes) ? notes : []).map((n) => ({
    title: n?.title ?? "",
    date: n?.date ?? "",
    show_in_planner: Boolean(n?.show_in_planner),
    planner_icon:
      typeof n?.planner_icon === "string" && n.planner_icon
        ? n.planner_icon
        : "production",
    auto_ref: n?.auto_ref ?? null,
    auto_stage: n?.auto_stage ?? null,
    done_date: n?.done_date ?? "",
  }));
}

const serviceLabel = (p) => (p?.invoice_date ? `Servicevertrag ${p.invoice_date.slice(0, 4)}` : "Servicevertrag");

function PaidDateRow({ value, onChange, testId }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_140px] gap-3 items-center">
      <div className="flex items-center text-[11px] text-slate-500 pl-1">Bezahlt am</div>
      <DateField
        value={value || ""}
        onChange={onChange}
        testId={testId}
        title="Bezahlt am"
      />
      <div />
    </div>
  );
}

export default function ProjectDialog({ open, onOpenChange, initial, onSave, onDelete, variant = "dialog", onBack }) {
  const isEdit = Boolean(initial?.id);
  const isPage = variant === "page";

  function addWeeks(isoDate, weeks) {
    const d = new Date(isoDate);
    if (isNaN(d)) return isoDate;
    d.setDate(d.getDate() + weeks * 7);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  const [form, setForm] = React.useState(initial ?? {
    name: "", client: "", start_date: "", end_date: "", slot: 1, status: "active",
    milestone_title: "", milestone_date: "",
    planner_short_label: "", planner_dimmed: false,
  });
  const formRef = React.useRef(null);
  const [payments, setPayments] = React.useState([emptyPayment(), emptyPayment(), emptyPayment()]);
  const [extraPayments, setExtraPayments] = React.useState([]);
  const [serviceContract, setServiceContract] = React.useState(emptyServiceContract());
  const [serviceEntries, setServiceEntries] = React.useState([]);
  const [planningNotes, setPlanningNotes] = React.useState(
    () => normalizePlanningNotes(initial?.planning_notes),
  );
  const [planningExtraOpen, setPlanningExtraOpen] = React.useState(isPage);
  const sectionCls = isPage ? "rounded-md border border-slate-200 p-4" : "border-t border-slate-100 pt-4";
  const sectionTitleCls = "text-xs font-semibold tracking-[0.15em] uppercase text-slate-700";
  const [showFinance, setShowFinance] = React.useState(false);

  // Track whether the user has manually edited end_date in this dialog session.
  const endTouchedRef = React.useRef(false);
  // Track whether the user manually edited the auto-derived due_date of each payment/SC row.
  // Standard payments: p0/p1/p2; service contract: sc; extras: x0/x1/...
  const dueTouchedRef = React.useRef({ p0: false, p1: false, p2: false, sc: false });

  React.useEffect(() => {
    if (open) {
      const init = initial ?? { name: "", client: "", start_date: "", end_date: "", slot: 1, status: "active", milestone_title: "", milestone_date: "", planner_short_label: "" };      // Normalize potentially absent milestone fields from server payload
      if (init.milestone_title == null) init.milestone_title = "";
      if (init.milestone_date == null) init.milestone_date = "";
      if (init.planner_short_label == null) init.planner_short_label = "";
      init.planner_dimmed = Boolean(init.planner_dimmed);
      const initEnd = init.end_date;
      const initStart = init.start_date;
      // For new projects (no id) with a prefilled start, default end to start + 8 weeks.
      if (!init.id && initStart && (!initEnd || initEnd === initStart)) {
        init.end_date = addWeeks(initStart, 8);
      }
      setForm(init);
      // For existing projects we treat end_date as user-defined.
      endTouchedRef.current = Boolean(init.id);

      // Payments: backfill to 3 rows with default labels
      const rawPayments = Array.isArray(init.payments) ? init.payments : [];
      const norm = [0, 1, 2].map((i) => {
        const p = rawPayments[i];
        const n = normalizePayment(p);
        if (!n.label) n.label = DEFAULT_PAYMENT_LABELS[i];
        return n;
      });
      setPayments(norm);
      // Extra payments
      const rawExtras = Array.isArray(init.extra_payments) ? init.extra_payments : [];
      const normExtras = rawExtras.map(normalizePayment);
      setExtraPayments(normExtras);
      // Mark existing extras as touched so user-saved due dates don't get overwritten
      const touched = { p0: true, p1: true, p2: true, sc: true };
      normExtras.forEach((_, i) => { touched[`x${i}`] = true; });
      setServiceContract(normalizeServiceContract(init.service_contract));
      const rawSce = Array.isArray(init.service_contract_entries) ? init.service_contract_entries : [];
      const normSce = rawSce.map(normalizePayment);
      setServiceEntries(normSce);
      // Planning notes (internal additional milestone list). We deliberately
      // pass the server-provided planner_icon through *unchanged* here.
      // Filtering against a local constant used to silently reset newly
      // introduced icons (e.g. "warning", "call") to "production" when an
      // older client loaded a project saved by a newer one. Backend
      // Pydantic Literal is the single source of truth for allowed values.
      setPlanningNotes(normalizePlanningNotes(init.planning_notes));
      setPlanningExtraOpen(false);
      // Reveal finance section if any data exists
      const anyPayment = rawPayments.some((p) => p && (p.amount != null || p.invoice_date || p.due_date));
      const anyExtra = rawExtras.some((p) => p && (p.amount != null || p.invoice_date || p.due_date || p.label));
      const hasSc = init.service_contract && (
        init.service_contract.yearly_amount != null ||
        init.service_contract.invoice_date ||
        init.service_contract.due_date
      );
      const anySce = rawSce.length > 0;
      setShowFinance(Boolean(anyPayment || anyExtra || hasSc || anySce || isPage));
      setPlanningExtraOpen(isPage);
      normSce.forEach((_, i) => { touched[`se${i}`] = true; });
      // The service-contract due-date is only "touched" if SC data actually exists;
      // otherwise the first manual entry should auto-derive due = invoice + 14 days,
      // matching all other payment rows.
      touched.sc = Boolean(hasSc);
      dueTouchedRef.current = touched;
      // On a fresh new dialog without finance data, due is "untouched" so auto-fill works
      if (!anyPayment && !hasSc && !anyExtra && !anySce) {
        dueTouchedRef.current = { p0: false, p1: false, p2: false, sc: false };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const update = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "start_date" && !endTouchedRef.current && v) {
        next.end_date = addWeeks(v, 8);
      }
      return next;
    });
  };

  const updateEnd = (v) => {
    endTouchedRef.current = true;
    setForm((f) => ({ ...f, end_date: v }));
  };

  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double submission
    if (!form.name || !form.client || !form.start_date || !form.end_date) return;
    if (form.end_date < form.start_date) return;
    const payload = {
      ...form,
      slot: Number(form.slot),
      milestone_title: form.milestone_title?.trim() ? form.milestone_title.trim() : null,
      planner_short_label: (() => {
        const v = (form.planner_short_label || "").replace(/^ +| +$/g, "");
        return v ? v.slice(0, 6) : null;
      })(),
      milestone_date: form.milestone_date || null,
      // Standardzahlungen sind positionsgebunden (Anzahlung/Zwischen/Schluss):
      // vollständig geleerte Zeilen als leeres Zahlungsobjekt senden, nicht als null.
      payments: payments.map((p) => paymentToPayload(p) || {
        ref: p.ref || null, label: null, amount: null, invoice_date: null, due_date: null, paid_date: null, status: "planned",
      }),
      extra_payments: extraPayments.map(paymentToPayload).filter(Boolean),
      service_contract: serviceContractToPayload(serviceContract),
      service_contract_entries: serviceEntries.map(paymentToPayload).filter(Boolean),
      planning_notes: dropOrphanAutoNotes(planningNotes, [
        ...payments.map((p) => p.ref),
        ...extraPayments.map((p) => p.ref),
        serviceContract.ref,
        ...serviceEntries.map((p) => p.ref),
      ])
        .map((n) => ({
          title: (n.title || "").trim(),
          date: n.date || null,
          show_in_planner: Boolean(n.show_in_planner),
          // Pass through as-is. Only fall back to the default when the
          // value is empty/missing (e.g. legacy note without planner_icon).
          // Backend Literal validation is the authoritative allow-list.
          planner_icon: (typeof n.planner_icon === "string" && n.planner_icon) || "production",
          auto_ref: n.auto_ref || null,
          auto_stage: n.auto_stage || null,
          done_date: n.done_date || null,
        }))
        .filter((n) => n.title),
    };
    try {
      setSubmitting(true);
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const updatePayment = (idx, key, value) => {
    const prevPay = payments[idx];
    const nextPay = { ...prevPay, [key]: value };
    if (key === "invoice_date" && value && (value !== prevPay?.invoice_date || !nextPay.due_date)) {
      nextPay.due_date = addDaysIso(value, 14);
    }
    if (key === "due_date") dueTouchedRef.current[`p${idx}`] = true;
    setPayments((prev) => prev.map((p, i) => (i === idx ? nextPay : p)));
    setPlanningNotes((n) => reconcileAutoNotes(n, prevPay, nextPay, nextPay.label || DEFAULT_PAYMENT_LABELS[idx]));
  };

  const updateExtra = (idx, key, value) => {
    const prevPay = extraPayments[idx];
    const nextPay = { ...prevPay, [key]: value };
    if (key === "invoice_date" && value && (value !== prevPay?.invoice_date || !nextPay.due_date)) {
      nextPay.due_date = addDaysIso(value, 14);
    }
    if (key === "due_date") dueTouchedRef.current[`x${idx}`] = true;
    setExtraPayments((prev) => prev.map((p, i) => (i === idx ? nextPay : p)));
    setPlanningNotes((n) => reconcileAutoNotes(n, prevPay, nextPay, nextPay.label || `Zusatz ${idx + 1}`));
  };

  const addExtraPayment = () => {
    setExtraPayments((prev) => {
      const next = [...prev, emptyPayment(`Zusatz ${prev.length + 1}`)];
      dueTouchedRef.current[`x${prev.length}`] = false;
      return next;
    });
  };

  const removeExtraPayment = (idx) => {
    setPlanningNotes((n) => removeOpenAutoNotes(n, extraPayments[idx]?.ref));
    setExtraPayments((prev) => prev.filter((_, i) => i !== idx));
    // Compact dueTouched flags for remaining extras
    const next = {};
    Object.keys(dueTouchedRef.current).forEach((k) => {
      if (!k.startsWith("x")) next[k] = dueTouchedRef.current[k];
    });
    dueTouchedRef.current = next;
  };

  const updateService = (key, value) => {
    const next = { ...serviceContract, [key]: value };
    if (key === "invoice_date" && value && (value !== serviceContract.invoice_date || !next.due_date)) {
      next.due_date = addDaysIso(value, 14);
    }
    if (key === "due_date") dueTouchedRef.current.sc = true;
    setServiceContract(next);
    setPlanningNotes((n) => reconcileAutoNotes(n, serviceContract, next, serviceLabel(next)));
  };

  const updateServiceEntry = (idx, key, value) => {
    const prevPay = serviceEntries[idx];
    const nextPay = { ...prevPay, [key]: value };
    if (key === "invoice_date" && value && (value !== prevPay?.invoice_date || !nextPay.due_date)) {
      nextPay.due_date = addDaysIso(value, 14);
    }
    if (key === "due_date") dueTouchedRef.current[`se${idx}`] = true;
    setServiceEntries((prev) => prev.map((p, i) => (i === idx ? nextPay : p)));
    setPlanningNotes((n) => reconcileAutoNotes(n, prevPay, nextPay, serviceLabel(nextPay)));
  };

  const addServiceEntry = () => {
    setServiceEntries((prev) => {
      // Suggest next-year invoice/due based on the latest known SC entry.
      const last = prev[prev.length - 1] || serviceContract;
      const invSrc = last && last.invoice_date ? last.invoice_date : "";
      const nextInv = invSrc ? addDaysIso(invSrc, 365) : "";
      const nextDue = nextInv ? addDaysIso(nextInv, 14) : "";
      const seed = {
        ...emptyPayment("Servicevertrag (jährlich)"),
        amount: serviceContract.yearly_amount || (last && last.amount) || "",
        invoice_date: nextInv,
        due_date: nextDue,
      };
      dueTouchedRef.current[`se${prev.length}`] = false;
      return [...prev, seed];
    });
  };

  const removeServiceEntry = (idx) => {
    setPlanningNotes((n) => removeOpenAutoNotes(n, serviceEntries[idx]?.ref));
    setServiceEntries((prev) => prev.filter((_, i) => i !== idx));
    // Compact dueTouched flags for remaining entries
    const next = {};
    Object.keys(dueTouchedRef.current).forEach((k) => {
      if (!k.startsWith("se")) next[k] = dueTouchedRef.current[k];
    });
    dueTouchedRef.current = next;
  };

  // Add a year to an ISO date, mirroring the backend rollover (handles Feb 29 → Feb 28).
  function addYearIso(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const target = new Date(y + 1, m - 1, d);
    if (target.getMonth() !== m - 1) {
      // Day overflow (e.g. Feb 29 → Mar 1) — snap to Feb 28 instead.
      return `${y + 1}-${String(m).padStart(2, "0")}-28`;
    }
    return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`;
  }

  // Reset an auto-generated service entry to its default values derived from the previous SC payment.
  const resetServiceEntry = (idx) => {
    const prevPay = idx === 0 ? serviceContract : serviceEntries[idx - 1];
    if (!prevPay || !prevPay.invoice_date) {
      // Nothing to derive from — abort silently.
      return;
    }
    const ok = window.confirm(
      "Diesen automatisch erzeugten Service-Eintrag auf Standardwerte zurücksetzen?",
    );
    if (!ok) return;
    const nextInv = addYearIso(prevPay.invoice_date);
    const nextDue = nextInv ? addDaysIso(nextInv, 14) : "";
    const amount = prevPay.amount !== "" && prevPay.amount != null
      ? prevPay.amount
      : (serviceContract.yearly_amount || "");
    setServiceEntries((prev) =>
      prev.map((p, i) =>
        i === idx
          ? {
              ...p,
              amount: amount === "" || amount == null ? "" : String(amount),
              invoice_date: nextInv,
              due_date: nextDue,
              paid_date: "",
              status: "planned",
            }
          : p,
      ),
    );
    // Re-mark due-date as untouched so subsequent invoice_date edits auto-fill again.
    dueTouchedRef.current[`se${idx}`] = false;
  };

  const handleKeyDown = (e) => {
    // Global shortcut: Ctrl/Cmd + Enter → trigger the same submit handler
    // as the "Speichern" button. Works even when focus is inside a Radix
    // Select (which normally swallows Enter).
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      formRef.current?.requestSubmit();
    }
  };

  const formBody = (
    <form ref={formRef} onSubmit={submit} className={isPage ? "space-y-6" : "space-y-4 pt-2"}>
      {isPage && (
        <button
          type="submit"
          disabled={submitting}
          className="fixed top-4 right-6 z-30 h-9 px-4 rounded-sm bg-slate-900 text-white text-xs tracking-wider uppercase shadow-sm hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="project-save-button-sticky"
          title="Speichern"
        >
          {submitting ? "Speichert…" : "Speichern"}
        </button>
      )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Projektname</Label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              className="h-9 mt-1"
              data-testid="project-name-input"
            />
          </div>

          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Projekt-Referenz</Label>
              <Input
                value={form.client}
                onChange={(e) => update("client", e.target.value)}
                required
                className="h-9 mt-1"
                data-testid="project-client-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Kürzel</Label>
              <Input
                value={form.planner_short_label || ""}
                onChange={(e) => update("planner_short_label", e.target.value.slice(0, 6))}
                maxLength={6}
                placeholder="z. B. PF DES"
                title="Optionales Kürzel für den horizontalen Planer (max. 6 Zeichen inkl. Leerzeichen)"
                className="h-9 mt-1 tabular-nums tracking-wide"
                data-testid="project-short-label-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Startdatum</Label>
              <DateField
                value={form.start_date}
                onChange={(iso) => update("start_date", iso)}
                className="mt-1"
                title="Startdatum"
                testId="project-start-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Enddatum</Label>
              <DateField
                value={form.end_date}
                onChange={(iso) => updateEnd(iso)}
                className="mt-1"
                title="Enddatum"
                testId="project-end-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Kapazitätsslot</Label>
              <Select value={String(form.slot)} onValueChange={(v) => update("slot", v)}>
                <SelectTrigger className="h-9 mt-1" data-testid="project-slot-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SLOTS.map((s) => (
                    <SelectItem key={s.n} value={String(s.n)}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-sm border border-slate-200"
                          style={{ backgroundColor: s.color }}
                        />
                        Slot {s.n} — {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="h-9 mt-1" data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2 flex items-start gap-2" data-testid="project-planner-dimmed-row">
                <Switch
                  id="planner-dimmed-switch"
                  checked={Boolean(form.planner_dimmed)}
                  onCheckedChange={(v) => update("planner_dimmed", Boolean(v))}
                  className="mt-0.5"
                  data-testid="project-planner-dimmed-switch"
                />
                <label htmlFor="planner-dimmed-switch" className="cursor-pointer select-none">
                  <div className="text-xs text-slate-700">Im Planer ausgrauen</div>
                  <div className="text-[10px] text-slate-400 leading-tight">Nur visuelle Markierung – der Projektstatus bleibt unverändert.</div>
                </label>
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="section-next-step">
            <div className={`${sectionTitleCls} mb-2`}>
              Nächster Arbeitsschritt (optional)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Titel</Label>
                <Input
                  value={form.milestone_title || ""}
                  onChange={(e) => update("milestone_title", e.target.value)}
                  className="h-9 mt-1"
                  maxLength={120}
                  placeholder="z. B. Erste Lieferung"
                  data-testid="project-milestone-title-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Datum</Label>
                <DateField
                  value={form.milestone_date}
                  onChange={(iso) => update("milestone_date", iso)}
                  className="mt-1"
                  title="Meilenstein-Datum"
                  testId="project-milestone-date-input"
                />
              </div>
            </div>
          </div>

          <div className={sectionCls} data-testid="section-customer-contact">
            <div className={`${sectionTitleCls} mb-2`}>
              Kundenkontakt &amp; Nachfassen
              <span className="ml-1 text-slate-400 normal-case tracking-normal font-normal">
                · Mit gesetztem Planer-Häkchen auch im Planer sichtbar.
              </span>
            </div>
            {(() => {
              const visibleRows =
                planningNotes.length === 0
                  ? [{ title: "", date: "" }] // always show one empty row
                  : planningExtraOpen ? planningNotes : planningNotes.slice(0, 1);
              const isEditingEmpty = planningNotes.length === 0;
              return (
                <div className="space-y-2" data-testid="planning-notes-section">
                  {visibleRows.map((row, idx) => (
                    <div key={idx}>
                    <div
                      className="grid grid-cols-[1fr_140px_140px_100px_36px] gap-2 items-center"
                      data-testid={`planning-note-row-${idx + 1}`}
                    >
                      <Input
                        value={row.title}
                        placeholder="Titel (z. B. Konzeptabnahme)"
                        maxLength={160}
                        className="h-9"
                        onChange={(e) => {
                          const val = e.target.value;
                          if (isEditingEmpty) {
                            setPlanningNotes(val ? [{ title: val, date: "", show_in_planner: false, planner_icon: "production" }] : []);
                          } else {
                            setPlanningNotes((prev) =>
                              prev.map((n, i) => (i === idx ? { ...n, title: val } : n)),
                            );
                          }
                        }}
                        data-testid={`planning-note-${idx + 1}-title-input`}
                      />
                      <DateField
                        value={row.date}
                        onChange={(iso) => {
                          if (isEditingEmpty) return;
                          setPlanningNotes((prev) =>
                            prev.map((n, i) => (i === idx ? { ...n, date: iso } : n)),
                          );
                        }}
                        title="Datum (optional)"
                        testId={`planning-note-${idx + 1}-date-input`}
                      />
                      {/* Symbol selector. Wichtig: der echte Radix-Select
                          wird ausschließlich für vorhandene Planungsnotizen
                          gemountet. Für die künstliche leere Eingabezeile
                          zeigen wir nur eine nicht-interaktive Platzhalter-
                          anzeige — sonst würde der Select mit dem Fallback
                          "production" mounten und beim späteren Übergang zu
                          einer echten Notiz (z. B. "call") den falschen Wert
                          via onValueChange in den State schreiben. */}
                      {isEditingEmpty ? (
                        <div
                          className="h-9 px-3 text-xs flex items-center gap-1.5 border border-slate-200 rounded-md bg-slate-50 text-slate-400 opacity-60"
                          data-testid={`planning-note-${idx + 1}-icon-placeholder`}
                          title="Symbol"
                        >
                          <PlanningIcon icon="production" size={12} color="#94a3b8" />
                          <span>Symbol</span>
                        </div>
                      ) : (
                        <Select
                          value={row.planner_icon}
                          onValueChange={(val) => {
                            setPlanningNotes((prev) =>
                              prev.map((n, i) => (i === idx ? { ...n, planner_icon: val } : n)),
                            );
                          }}
                        >
                          <SelectTrigger
                            className="h-9 text-xs"
                            data-testid={`planning-note-${idx + 1}-icon-select`}
                            title="Symbol"
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <PlanningIcon icon={row.planner_icon} size={12} />
                              <SelectValue />
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {PLANNING_ICON_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <span className="inline-flex items-center gap-2">
                                  <PlanningIcon icon={opt.value} size={12} />
                                  {opt.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {/* Im Planer anzeigen */}
                      <label
                        className={`flex items-center gap-1.5 text-[11px] text-slate-600 ${isEditingEmpty ? "opacity-50" : ""}`}
                        title="Im Planer anzeigen"
                      >
                        <Checkbox
                          checked={Boolean(row.show_in_planner)}
                          disabled={isEditingEmpty}
                          onCheckedChange={(v) => {
                            if (isEditingEmpty) return;
                            setPlanningNotes((prev) =>
                              prev.map((n, i) => (i === idx ? { ...n, show_in_planner: Boolean(v) } : n)),
                            );
                          }}
                          data-testid={`planning-note-${idx + 1}-show-in-planner`}
                        />
                        <span>Planer</span>
                      </label>
                      {isEditingEmpty ? (
                        <div />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setPlanningNotes((prev) => prev.filter((_, i) => i !== idx));
                          }}
                          className="h-9 w-9 grid place-items-center rounded-sm text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          aria-label="Entfernen"
                          data-testid={`planning-note-${idx + 1}-remove-button`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {row.auto_ref && (
                      <div
                        className="mt-1 ml-1 flex items-center gap-3 text-[10px] text-slate-500"
                        data-testid={`planning-note-${idx + 1}-auto-info`}
                      >
                        <span className="uppercase tracking-wider text-slate-400">Automatisch</span>
                        <span className="inline-flex items-center gap-1.5">
                          {row.auto_stage === "m1" ? "Versandt am" : "Erledigt am"}
                          <DateField
                            value={row.done_date || ""}
                            onChange={(iso) => {
                              setPlanningNotes((prev) => {
                                const next = prev.map((n, i) => (i === idx ? { ...n, done_date: iso || "" } : n));
                                return row.auto_stage === "m1" ? applyM1Sent(next, row.auto_ref, iso || "") : next;
                              });
                            }}
                            className="w-[120px]"
                            inputClassName="h-7 text-xs"
                            title={row.auto_stage === "m1" ? "Tatsächlicher Versand der Mahnung" : "Erledigt / versandt am"}
                            testId={`planning-note-${idx + 1}-done-date-input`}
                          />
                        </span>
                        {deadlineLabel(row) && (
                          <span className="font-medium text-slate-700" data-testid={`planning-note-${idx + 1}-deadline`}>
                            {deadlineLabel(row)}
                          </span>
                        )}
                      </div>
                    )}
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between pt-1">
                    {planningNotes.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setPlanningExtraOpen((v) => !v)}
                        className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                        data-testid="planning-notes-toggle"
                      >
                        {planningExtraOpen
                          ? "weniger anzeigen"
                          : `weitere anzeigen (${planningNotes.length - 1})`}
                      </button>
                    ) : <span />}
                    <button
                      type="button"
                      onClick={() => {
                        setPlanningNotes((prev) => [...prev, { title: "", date: "", show_in_planner: false, planner_icon: "production" }]);
                        setPlanningExtraOpen(true);
                      }}
                      className="text-[11px] text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                      data-testid="planning-note-add-button"
                    >
                      + Eintrag hinzufügen
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className={sectionCls} data-testid="section-payments">
            <button
              type="button"
              onClick={() => setShowFinance((s) => !s)}
              className={`w-full flex items-center justify-between ${sectionTitleCls} hover:text-slate-900`}
              data-testid="project-finance-toggle"
            >
              <span>Zahlungen &amp; Fälligkeiten (optional)</span>
              {showFinance ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {showFinance && (
              <div className="mt-3 space-y-4">
                {payments.map((pay, idx) => (
                  <div key={idx} className="space-y-2" data-testid={`payment-row-${idx + 1}`}>
                    <div className="text-[11px] text-slate-500 font-medium">
                      Zahlung {idx + 1}
                    </div>
                    <div className="grid grid-cols-[1fr_120px] gap-3">
                      <Input
                        value={pay.label}
                        onChange={(e) => updatePayment(idx, "label", e.target.value)}
                        placeholder={DEFAULT_PAYMENT_LABELS[idx]}
                        className="h-9"
                        maxLength={80}
                        data-testid={`payment-${idx + 1}-label-input`}
                      />
                      <Input
                        type="number"
                        value={pay.amount}
                        onChange={(e) => updatePayment(idx, "amount", e.target.value)}
                        placeholder="Betrag €"
                        className="h-9"
                        min="0"
                        step="0.01"
                        data-testid={`payment-${idx + 1}-amount-input`}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_140px] gap-3 items-end">
                      <DateField
                        value={pay.invoice_date}
                        onChange={(iso) => updatePayment(idx, "invoice_date", iso)}
                        testId={`payment-${idx + 1}-invoice-date-input`}
                        title="Rechnungsdatum" label="Rechnungsdatum"
                      />
                      <DateField
                        value={pay.due_date}
                        onChange={(iso) => updatePayment(idx, "due_date", iso)}
                        testId={`payment-${idx + 1}-due-date-input`}
                        title="Fällig am" label="Fällig am"
                      />
                      <Select
                        value={pay.status}
                        onValueChange={(v) => updatePayment(idx, "status", v)}
                      >
                        <SelectTrigger
                          className="h-9"
                          data-testid={`payment-${idx + 1}-status-select`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {pay.status === "paid" && (
                      <PaidDateRow
                        value={pay.paid_date}
                        onChange={(iso) => updatePayment(idx, "paid_date", iso)}
                        testId={`payment-${idx + 1}-paid-date-input`}
                      />
                    )}
                  </div>
                ))}

                <div
                  className="border-t border-slate-100 pt-4 space-y-3"
                  data-testid="extra-payments-section"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="text-[11px] text-slate-500 font-medium">
                      Zusatz-Rechnungen ({extraPayments.length})
                    </div>
                    <button
                      type="button"
                      onClick={addExtraPayment}
                      className="text-[11px] text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                      data-testid="add-extra-payment-button"
                    >
                      + Zusatz hinzufügen
                    </button>
                  </div>
                  {extraPayments.map((pay, idx) => (
                    <div key={idx} className="space-y-2" data-testid={`extra-payment-row-${idx + 1}`}>
                      <div className="grid grid-cols-[1fr_120px_36px] gap-3 items-center">
                        <Input
                          value={pay.label}
                          onChange={(e) => updateExtra(idx, "label", e.target.value)}
                          placeholder={`Zusatz ${idx + 1}`}
                          className="h-9"
                          maxLength={80}
                          data-testid={`extra-payment-${idx + 1}-label-input`}
                        />
                        <Input
                          type="number"
                          value={pay.amount}
                          onChange={(e) => updateExtra(idx, "amount", e.target.value)}
                          placeholder="Betrag €"
                          className="h-9"
                          min="0"
                          step="0.01"
                          data-testid={`extra-payment-${idx + 1}-amount-input`}
                        />
                        <button
                          type="button"
                          onClick={() => removeExtraPayment(idx)}
                          className="h-9 w-9 grid place-items-center rounded-sm text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          aria-label="Zusatz entfernen"
                          data-testid={`extra-payment-${idx + 1}-remove-button`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-[1fr_1fr_140px] gap-3 items-end">
                        <DateField
                          value={pay.invoice_date}
                          onChange={(iso) => updateExtra(idx, "invoice_date", iso)}
                          testId={`extra-payment-${idx + 1}-invoice-date-input`}
                          title="Rechnungsdatum" label="Rechnungsdatum"
                        />
                        <DateField
                          value={pay.due_date}
                          onChange={(iso) => updateExtra(idx, "due_date", iso)}
                          testId={`extra-payment-${idx + 1}-due-date-input`}
                          title="Fällig am" label="Fällig am"
                        />
                        <Select value={pay.status} onValueChange={(v) => updateExtra(idx, "status", v)}>
                          <SelectTrigger className="h-9" data-testid={`extra-payment-${idx + 1}-status-select`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {pay.status === "paid" && (
                        <PaidDateRow
                          value={pay.paid_date}
                          onChange={(iso) => updateExtra(idx, "paid_date", iso)}
                          testId={`extra-payment-${idx + 1}-paid-date-input`}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div
                  className="border-t border-slate-100 pt-4 space-y-2"
                  data-testid="service-contract-row"
                >
                  <div className="text-[11px] text-slate-500 font-medium">
                    Servicevertrag (jährlich)
                  </div>
                  <div className="grid grid-cols-[1fr_120px] gap-3">
                    <div className="flex items-center text-xs text-slate-500 pl-1">
                      Jährliche Servicepauschale
                    </div>
                    <Input
                      type="number"
                      value={serviceContract.yearly_amount}
                      onChange={(e) => updateService("yearly_amount", e.target.value)}
                      placeholder="Betrag €/Jahr"
                      className="h-9"
                      min="0"
                      step="0.01"
                      data-testid="service-yearly-amount-input"
                    />
                  </div>
                  <div className="grid grid-cols-[1fr_1fr_140px] gap-3 items-end">
                    <DateField
                      value={serviceContract.invoice_date}
                      onChange={(iso) => updateService("invoice_date", iso)}
                      testId="service-invoice-date-input"
                      title="Rechnungsdatum" label="Rechnungsdatum"
                    />
                    <DateField
                      value={serviceContract.due_date}
                      onChange={(iso) => updateService("due_date", iso)}
                      testId="service-due-date-input"
                      title="Fällig am" label="Fällig am"
                    />
                    <Select
                      value={serviceContract.status}
                      onValueChange={(v) => updateService("status", v)}
                    >
                      <SelectTrigger className="h-9" data-testid="service-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {serviceContract.status === "paid" && (
                    <PaidDateRow
                      value={serviceContract.paid_date}
                      onChange={(iso) => updateService("paid_date", iso)}
                      testId="service-paid-date-input"
                    />
                  )}

                  {(serviceEntries.length > 0 || serviceContract.invoice_date) && (
                    <div className="pt-2 space-y-2" data-testid="service-entries-section">
                      <div className="flex items-baseline justify-between">
                        <div className="text-[11px] text-slate-500">
                          Weitere Jahre ({serviceEntries.length})
                          <span className="text-slate-400 ml-1.5">
                            — wird beim Bezahlen automatisch fortgeschrieben
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={addServiceEntry}
                          className="text-[11px] text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                          data-testid="add-service-entry-button"
                        >
                          + Jahr hinzufügen
                        </button>
                      </div>
                      {serviceEntries.map((pay, idx) => (
                        <div
                          key={idx}
                          className="space-y-2"
                          data-testid={`service-entry-row-${idx + 1}`}
                        >
                          <div className="grid grid-cols-[120px_1fr_1fr_140px_32px_32px] gap-2 items-end">
                          <Input
                            type="number"
                            value={pay.amount}
                            onChange={(e) => updateServiceEntry(idx, "amount", e.target.value)}
                            placeholder="Betrag €"
                            className="h-9"
                            min="0"
                            step="0.01"
                            data-testid={`service-entry-${idx + 1}-amount-input`}
                          />
                          <DateField
                            value={pay.invoice_date}
                            onChange={(iso) => updateServiceEntry(idx, "invoice_date", iso)}
                            testId={`service-entry-${idx + 1}-invoice-date-input`}
                            title="Rechnungsdatum" label="Rechnungsdatum"
                          />
                          <DateField
                            value={pay.due_date}
                            onChange={(iso) => updateServiceEntry(idx, "due_date", iso)}
                            testId={`service-entry-${idx + 1}-due-date-input`}
                            title="Fällig am" label="Fällig am"
                          />
                          <Select
                            value={pay.status}
                            onValueChange={(v) => updateServiceEntry(idx, "status", v)}
                          >
                            <SelectTrigger className="h-9" data-testid={`service-entry-${idx + 1}-status-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PAYMENT_STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => resetServiceEntry(idx)}
                            className="h-9 w-8 grid place-items-center rounded-sm text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                            aria-label="Eintrag zurücksetzen"
                            title="Auf Standardwerte zurücksetzen"
                            data-testid={`service-entry-${idx + 1}-reset-button`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeServiceEntry(idx)}
                            className="h-9 w-8 grid place-items-center rounded-sm text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            aria-label="Jahr entfernen"
                            data-testid={`service-entry-${idx + 1}-remove-button`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          </div>
                          {pay.status === "paid" && (
                            <div className="grid grid-cols-[120px_1fr_1fr_140px_32px_32px] gap-2 items-end">
                              <div className="text-[11px] text-slate-500 pl-1">Bezahlt am</div>
                              <DateField
                                value={pay.paid_date || ""}
                                onChange={(iso) => updateServiceEntry(idx, "paid_date", iso)}
                                testId={`service-entry-${idx + 1}-paid-date-input`}
                                title="Bezahlt am"
                              />
                              <div /><div /><div /><div />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-2">
                    <Label className="text-[11px] text-slate-500 font-medium">
                      Service-Notiz
                    </Label>
                    <Textarea
                      value={serviceContract.note ?? ""}
                      onChange={(e) => updateService("note", e.target.value)}
                      placeholder="z. B. Jährliche Abrechnung künftig ab Onlinegang / voraussichtlich 15.08."
                      rows={2}
                      maxLength={500}
                      className="mt-1 text-sm"
                      data-testid="service-note-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className={isPage
            ? "pt-4 flex flex-row justify-between gap-2 border-t border-slate-100"
            : "pt-2 flex flex-row sm:justify-between gap-2"}>
            <div>
              {isEdit && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onDelete?.(initial.id)}
                  className="h-9 text-rose-600 hover:bg-rose-50 border-rose-200"
                  data-testid="project-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Löschen
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => (isPage ? onBack?.() : onOpenChange(false))}
                className="h-9"
                data-testid="project-cancel-button"
              >
                {isPage ? "Zurück" : "Abbrechen"}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="project-save-button"
              >
                {submitting ? "Speichert…" : "Speichern"}
              </Button>
            </div>
          </DialogFooter>
        </form>
  );

  if (isPage) {
    return (
      <div
        className="mx-auto w-full max-w-6xl px-6 py-6"
        data-testid="project-detail-page-inner"
        onKeyDownCapture={handleKeyDown}
      >
        {formBody}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] bg-white border border-slate-200 max-h-[90vh] overflow-y-auto"
        data-testid="project-dialog"
        onKeyDownCapture={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle
            className="text-xl tracking-tight font-semibold text-slate-900"
            style={{ fontFamily: "Manrope, ui-sans-serif" }}
          >
            {isEdit ? "Projekt bearbeiten" : "Projekt erstellen"}
          </DialogTitle>
        </DialogHeader>
        {formBody}
      </DialogContent>
    </Dialog>
  );
}
